import { useEffect, useRef } from 'react';

const MIN_DATE = '1900-01-01';
const MAX_DATE = '2999-12-31';

export interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'defaultValue' | 'onChange'> {
    //yyyy-MM-dd or an empty string
    value: string;
    //Called when a date is picked in the date picker, or typed and confirmed with Enter or by leaving the field
    onChange: (date: string) => void;
}

export const DateInput = ({ value, onChange, onBlur, onClick, onKeyDown, onMouseDown, min, max, ...props }: DateInputProps) => {
    const ref = useRef<HTMLInputElement>(null);
    //A typed date is applied on Enter or blur instead of on every key press
    const isTyping = useRef(false);
    //Only the click that focuses the field opens the date picker, second click allows typing, this was supported in Pikaday.
    const wasFocused = useRef(false);

    //The input is uncontrolled so React never overwrites a half-typed date, but it follows value when it changes from outside
    useEffect(() => {
        if (ref.current && ref.current.value !== value) {
            ref.current.value = value;
        }
    }, [value]);

    const applyDate = (input: HTMLInputElement) => {
        isTyping.current = false;
        if (!input.validity.valid) {
            //Reset input to the last valid date
            input.value = value;
        } else if (input.value !== value) {
            onChange(input.value);
        }
    };

    //onChange fires for every input event, also while browsing in Chrome's date picker,
    //but the native change event fires once a date is picked
    useEffect(() => {
        const input = ref.current;
        const handleChange = () => {
            if (!isTyping.current) {
                applyDate(input);
            }
        };
        input.addEventListener('change', handleChange);
        return () => input.removeEventListener('change', handleChange);
    });

    const handleMouseDown = (event: React.MouseEvent<HTMLInputElement>) => {
        wasFocused.current = document.activeElement === event.currentTarget;
        onMouseDown?.(event);
    };

    const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
        isTyping.current = false;
        if (!wasFocused.current) {
            try {
                //Chrome only opens the date picker from the calendar icon
                event.currentTarget.showPicker();
            } catch {
                //The browser doesn't support showPicker, have to click calendar icon manually
            }
        }
        onClick?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            applyDate(event.currentTarget);
        } else {
            isTyping.current = true;
        }
        onKeyDown?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        applyDate(event.currentTarget);
        onBlur?.(event);
    };

    return (
        <input
            {...props}
            ref={ref}
            type="date"
            defaultValue={value}
            min={min || MIN_DATE}
            max={max || MAX_DATE}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
        />
    );
};
